import React from "react";
import { Input } from "antd";
import { RotateCcw } from "react-feather";

import SpainFlag from "../../../../assets/images/flags/spain.svg";
import FranceFlag from "../../../../assets/images/flags/france.svg";
import GermanyFlag from "../../../../assets/images/flags/germany.svg";
import PortugalFlag from "../../../../assets/images/flags/portugal.svg";
import BrazilFlag from "../../../../assets/images/flags/brazil.svg";
import { convertToUserTimezone } from "../../../../utils/dateService";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";

import type { SportsbookTranslationEditor } from "./useSportsbookTranslationEditor";
import "./stylesTranslations.css";

export interface TranslationFieldProps {
  editor: SportsbookTranslationEditor;
}

type LocalePresentation = {
  label: string;
  flag?: string;
};

function getLocalePresentation(locale: string): LocalePresentation {
  switch (locale.toLowerCase()) {
    case "es":
      return { label: AppStrings.SPORTSBOOK_FM_SPANISH, flag: SpainFlag };
    case "fr":
      return { label: AppStrings.SPORTSBOOK_FM_FRENCH, flag: FranceFlag };
    case "de":
      return { label: AppStrings.SPORTSBOOK_FM_GERMAN, flag: GermanyFlag };
    case "pt":
      return {
        label: AppStrings.SPORTSBOOK_FM_PORTUGUESE,
        flag: PortugalFlag,
      };
    case "pt-br":
      return {
        label: AppStrings.SPORTSBOOK_FM_PORTUGUESE_BRAZIL,
        flag: BrazilFlag,
      };
    case "pt-pt":
      return {
        label: AppStrings.SPORTSBOOK_FM_PORTUGUESE_PORTUGAL,
        flag: PortugalFlag,
      };
    default:
      return { label: locale };
  }
}

function formatOverrideTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return timestamp;
  return convertToUserTimezone(timestamp, "MMM D, YYYY h:mm A");
}

function getTranslationStatusLabel(
  status: NonNullable<
    SportsbookTranslationEditor["selectedTranslation"]
  >["status"],
): string {
  switch (status) {
    case "translated":
      return AppStrings.SPORTSBOOK_FM_STATUS_TRANSLATED;
    case "pending":
      return AppStrings.SPORTSBOOK_FM_STATUS_PENDING;
    case "missing":
      return AppStrings.SPORTSBOOK_FM_STATUS_MISSING;
    case "stale":
      return AppStrings.SPORTSBOOK_FM_STATUS_STALE;
  }
}

export function TranslationField({
  editor,
}: TranslationFieldProps): React.ReactElement | null {
  if (!editor.enabled) return null;

  const {
    entityKey,
    query,
    data,
    locales,
    selectedLocale,
    selectedTranslation,
    selectedValue,
    selectedIsDirty,
    hasEmptyTranslationDraft,
    isMutating,
    mutationError,
    selectLocale,
    changeDraft,
    resetDraft,
    confirmStale,
    discardOverride,
  } = editor;

  return (
    <div className="future-tab-translation-field">
      <label htmlFor="editFutureTabTranslation">
        {AppStrings.SPORTSBOOK_FM_TRANSLATION}
      </label>
      {!entityKey ? (
        <p
          className="future-tab-translation-state"
          data-testid="future-tab-translations-unavailable"
        >
          {AppStrings.SPORTSBOOK_FM_TRANSLATIONS_UNAVAILABLE}
        </p>
      ) : query.isLoading && !data ? (
        <p
          className="future-tab-translation-state"
          data-testid="future-tab-translations-loading"
          role="status"
          aria-live="polite"
        >
          {AppStrings.SPORTSBOOK_FM_LOADING_TRANSLATIONS}
        </p>
      ) : query.isError && !data ? (
        <div
          className="future-tab-translation-state"
          data-testid="future-tab-translations-error"
        >
          <span>{AppStrings.SPORTSBOOK_FM_LOAD_TRANSLATIONS_ERROR}</span>
          <button type="button" onClick={() => query.refetch()}>
            {AppStrings.SPORTSBOOK_FM_RETRY}
          </button>
        </div>
      ) : data && locales.length === 0 ? (
        <div data-testid="future-tab-translations-empty">
          <p className="future-tab-translation-state">
            {AppStrings.SPORTSBOOK_FM_NO_CONFIGURED_LOCALES}
          </p>
        </div>
      ) : selectedTranslation && data ? (
        <>
          <div
            className="future-tab-locale-chips"
            role="group"
            aria-label={AppStrings.SPORTSBOOK_FM_TRANSLATION_LOCALES}
          >
            {locales.map((locale) => {
              const presentation = getLocalePresentation(locale.locale);
              return (
                <button
                  key={locale.locale}
                  type="button"
                  className={`future-tab-locale-chip ${
                    selectedLocale === locale.locale ? "selected" : ""
                  }`}
                  aria-pressed={selectedLocale === locale.locale}
                  aria-label={formatString(
                    AppStrings.SPORTSBOOK_FM_SELECT_LOCALE_ARIA,
                    { locale: locale.locale },
                  )}
                  onClick={() => selectLocale(locale.locale)}
                >
                  {presentation.flag && (
                    <span
                      className="future-tab-locale-flag-slot"
                      data-testid={`future-tab-locale-flag-slot-${locale.locale}`}
                    >
                      <img
                        src={presentation.flag}
                        alt=""
                        aria-hidden="true"
                        data-testid={`future-tab-locale-flag-${locale.locale}`}
                      />
                    </span>
                  )}
                  <span>{presentation.label}</span>
                </button>
              );
            })}
          </div>
          <div className="future-tab-translation-input">
            <Input
              id="editFutureTabTranslation"
              placeholder={AppStrings.SPORTSBOOK_FM_NO_TRANSLATION}
              value={selectedValue}
              disabled={isMutating}
              onChange={(event) => changeDraft(event.target.value)}
            />
            <button
              type="button"
              className="future-tab-translation-reset"
              aria-label={formatString(
                AppStrings.SPORTSBOOK_FM_RESET_TRANSLATION_ARIA,
                { locale: selectedTranslation.locale },
              )}
              disabled={isMutating}
              onClick={resetDraft}
            >
              <RotateCcw size={16} />
            </button>
          </div>
          {selectedTranslation.status !== "translated" && (
            <p
              className={`future-tab-translation-status ${selectedTranslation.status}`}
              data-testid="future-tab-translation-status"
              role="status"
              aria-live="polite"
            >
              {getTranslationStatusLabel(selectedTranslation.status)}
            </p>
          )}
          {selectedTranslation.override !== null &&
            selectedTranslation.status !== "stale" && (
              <button
                type="button"
                className="future-tab-remove-override"
                aria-label={formatString(
                  AppStrings.SPORTSBOOK_FM_REMOVE_OVERRIDE_ARIA,
                  { locale: selectedTranslation.locale },
                )}
                disabled={isMutating || selectedIsDirty}
                onClick={discardOverride}
              >
                {AppStrings.SPORTSBOOK_FM_REMOVE_OVERRIDE}
              </button>
            )}
          {selectedTranslation.status === "stale" && (
            <div
              className="future-tab-stale-context"
              data-testid="future-tab-stale-context"
            >
              <p>{AppStrings.SPORTSBOOK_FM_STALE_TRANSLATION_DESCRIPTION}</p>
              <p>
                {formatString(AppStrings.SPORTSBOOK_FM_MACHINE_VALUE, {
                  value:
                    selectedTranslation.machine ??
                    AppStrings.SPORTSBOOK_FM_NO_TRANSLATION,
                })}
              </p>
              {selectedTranslation.override_meta && (
                <p>
                  {formatString(AppStrings.SPORTSBOOK_FM_OVERRIDE_METADATA, {
                    user: selectedTranslation.override_meta.updated_by,
                    date: formatOverrideTimestamp(
                      selectedTranslation.override_meta.updated_at,
                    ),
                  })}
                </p>
              )}
              <div className="future-tab-stale-actions">
                <button
                  type="button"
                  disabled={
                    isMutating ||
                    selectedIsDirty ||
                    selectedTranslation.override === null
                  }
                  aria-label={formatString(
                    AppStrings.SPORTSBOOK_FM_CONFIRM_OVERRIDE_ARIA,
                    { locale: selectedTranslation.locale },
                  )}
                  onClick={confirmStale}
                >
                  {AppStrings.SPORTSBOOK_FM_CONFIRM_OVERRIDE}
                </button>
                <button
                  type="button"
                  disabled={isMutating || selectedIsDirty}
                  aria-label={formatString(
                    AppStrings.SPORTSBOOK_FM_DISCARD_OVERRIDE_ARIA,
                    { locale: selectedTranslation.locale },
                  )}
                  onClick={discardOverride}
                >
                  {AppStrings.SPORTSBOOK_FM_DISCARD_OVERRIDE}
                </button>
              </div>
            </div>
          )}
        </>
      ) : null}
      {hasEmptyTranslationDraft && (
        <p className="future-tab-translation-error" role="alert">
          {AppStrings.SPORTSBOOK_FM_EMPTY_TRANSLATION_ERROR}
        </p>
      )}
      {mutationError && (
        <p
          className="future-tab-translation-error"
          role="alert"
          data-testid="future-tab-translation-mutation-error"
        >
          {mutationError}
        </p>
      )}
    </div>
  );
}
